import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { CandidateAccount } from '@ats/api/cm';
import { CmSettingsService } from '@ats/cm/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreqGuardData } from '../models';

const SP_CRCAT_QUERY = gql`
	query candidateCReq($candidate: CandidateIdRequest, $creq: RequestByIdOrExternalId!) {
		candidateAccount(request: $candidate) {
			cmcId
			certRequirementDetail(request: $creq) {
				id
				cmcId
				certRequirementCatalog {
					id
					type
				}
			}
		}
	}
`;

const CP_CRCAT_QUERY = gql`
	query candidateCReq($creq: RequestByIdOrExternalId!) {
		candidateAccount {
			cmcId
			certRequirementDetail(request: $creq) {
				id
				cmcId
				certRequirementCatalog {
					id
					type
				}
			}
		}
	}
`;

@Injectable()
export class CmCreqTypeGuard implements CanActivate {
	constructor(
		private readonly apollo: Apollo,
		private readonly router: Router,
		private readonly settings: CmSettingsService
	) {}

	canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
		return this.checkCreqType$(route);
	}

	private checkCreqType$(route: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
		const creqGuardData: CreqGuardData = (route.data || {}).creqGuard || null;
		const creqId = +route.paramMap.get(creqGuardData.idParam);
		const candidateId = this.getCandidateId(route, (creqGuardData || {}).candidateIdParam);

		if (!creqGuardData || !creqGuardData.allowedTypes || !creqId) {
			throw new Error('CmCreqTypeGuard missing critical data on route');
		}

		const variables = {
			creq: {
				id: creqId,
			},
		};

		if (candidateId !== null) {
			variables['candidate'] = {
				cmcId: candidateId,
			};
		}

		return this.apollo
			.query<{ candidateAccount: Pick<CandidateAccount, 'cmcId' | 'certRequirementDetail'> }>(
				{
					query: candidateId !== null ? SP_CRCAT_QUERY : CP_CRCAT_QUERY,
					variables,
				}
			)
			.pipe(
				map(
					({ data }) =>
						(
							((data.candidateAccount || {}).certRequirementDetail || {})
								.certRequirementCatalog || {}
						).type
				),
				map(
					type =>
						(!!type && creqGuardData.allowedTypes.includes(type)) ||
						this.router.createUrlTree(this.settings.route(['not-found']))
				)
			);
	}

	private getCandidateId(route: ActivatedRouteSnapshot, param: string): number | null {
		if (!param) {
			return null;
		}

		if (route.paramMap.has(param)) {
			return +route.paramMap.get(param);
		}

		if (route.parent) {
			return this.getCandidateId(route.parent, param);
		}

		return null;
	}
}
